import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps
} from 'recharts';
import { InventoryApiService } from '../../Service/InventoryApiService';
import { CommonService } from '../../Service/CommonService';
import Spinner from '../Common/Spinner';
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import Select from "react-select";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import "../Dynamic/MarketValueHistory.scss";
import { IconButton } from '@mui/material';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// Define the data structure type
type DataPoint = {
  quarter: string;
  year: string;
  currentInsuranceValue: number;
  recommendedInsuranceValue: number;
  currentMarketValuePerBarrel: number;
  recommendedMarketValuePerBarrel: number;
};

const BarrelMarketValueChartView = () => {
  const { id, lotId } = useParams<{ id:any, lotId: any }>();
  const [data, setData] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState<string>("Current year");
  const [hoveredBar, setHoveredBar] = useState<{data: DataPoint, index: number} | null>(null);
  const [lotsList, setLotsList] = useState<any[]>([]);
  const [selectedLotId, setSelectedLotId] = useState<string>(lotId);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const params = id ? id : CommonService.getCompanyId();
  const history = useHistory();
  
  // Format numbers for tooltip
  const formatCurrency = (value: number): string => {
    return `$${value.toLocaleString()}`;
  };

  const getMarketValueData = async (lotId: any, year:any) => {
    try {
      setIsLoading(true);
      const response = await InventoryApiService.getMarketValueWithFilter(params, lotId, year);

      // Process the data to calculate total values
      const processedData = response.data.map(item => {
        const recommendedInsuranceValue = item.quantity * item.recommendedMarketValuePerBarrel;
        const currentInsuranceValueRaw = item.currentMarketValuePerBarrel;
        const currentInsuranceValue = currentInsuranceValueRaw != null
          ? item.quantity * currentInsuranceValueRaw
          : recommendedInsuranceValue; // fallback to recommended value

        return {
          ...item,
          recommendedInsuranceValue,
          currentInsuranceValue,
          // Format quarter for display (Q1, Q2, etc.)
          quarterDisplay: `Q${item.quarter} ${getQuarterLabel(item.quarter)}`,
        };
      });

      // Sort data by year and quarter (most recent first)
      const sortedData = processedData.sort((a, b) => {
        if (b.year !== a.year) return a.year - b.year;
        return a.quarter - b.quarter;
      });

      setData(sortedData);
    } catch (error) {
      console.log("Issue occurred while API calling");
    } finally {
      setIsLoading(false);
    }
  };

  
  // Update filter handler
  // const handleFilterChange = (e) => {
  //   const filterValue = e.target.value;
  //   setSelectedFilter(filterValue);
    
  //   // Set lastNYear based on filter selection
  //   switch(filterValue) {
  //     case "Current year":
  //       setLastNYear(0);
  //       break;
  //     case "Last year":
  //       setLastNYear(1);
  //       break;
  //     case "Last 2 years":
  //       setLastNYear(2);
  //       break;
  //     case "Last 3 years":
  //     default:
  //       setLastNYear(3);
  //       break;
  //   }
  // };

  // Effect to refetch data when filter changes
  useEffect(() => {
    getMarketValueData(lotId, selectedYear);
  }, []);

  // Fetch all lots for the company
  const getAllLotsByCompany = async () => {
    try {
      const companyId = CommonService.getCompanyId();
      const response = await InventoryApiService.getAllLotNoByCompanyId(params);
      // ensure it's always an array
      const normalizedData = response.data
      setLotsList(normalizedData);
    } catch (error) {
      console.log("Issue occurred while fetching lots");
    } finally {
      setIsLoading(false);
    }
  };

   // Handle barrel lot selection change
  const handleBarrelLotChange = (event) => {
    const selectedLot = event.target.value;
    setSelectedLotId(selectedLot);
    getMarketValueData(selectedLot, selectedYear);
  };


  // Initialize component - fetch lots and set default lot
  useEffect(() => {
    getAllLotsByCompany();
    setSelectedLotId(lotId);
  }, []);

  const getQuarterLabel = (quarter) => {
    const labels = {
      1: '(Jan - Mar)',
      2: '(Apr - Jun)',
      3: '(Jul - Sep)',
      4: '(Oct - Dec)',
    };
    return labels[quarter] || '';
  };

  // Custom tooltip content with proper type definitions
   const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>): JSX.Element | null => {
    if (active && payload && payload.length && payload[0].payload) {
      const data = payload[0].payload;
      const dataKey = payload[0].dataKey as string;
      
      return (
        <div className="custom-tooltip" style={{
          backgroundColor: 'white',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '5px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
          minWidth: '180px',
          maxWidth:'220px'
        }}>
          <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '8px' }}>
            Q{data.quarter} {getQuarterLabel(data.quarter)} {data.year}
          </div>
          
          <table style={{ width: '100%', fontSize: '13px' }}>
            <tbody>
              <tr>
                <td>Quantity:</td>
                <td style={{ textAlign: 'right', fontWeight: '500' }}>
                  {data.quantity.toLocaleString()} Container
                </td>
              </tr>
              
              {dataKey === 'currentInsuranceValue' && (
                <>
                  <tr>
                    <td>Per Container Current Market Value:</td>
                    <td style={{ textAlign: 'right', fontWeight: '500' }}>
                      ${Number(
                        data.currentMarketValuePerBarrel != null 
                          ? data.currentMarketValuePerBarrel 
                          : data.recommendedMarketValuePerBarrel
                      ).toFixed(2)}
                    </td>
                  </tr>
                  <tr>
                    <td>Total Current Insurance Value:</td>
                    <td style={{ textAlign: 'right', fontWeight: '500' }}>
                      {formatCurrency(data.currentInsuranceValue)}
                    </td>
                  </tr>
                </>
              )}
              
              {true && (
                <>
                  <tr>
                    <td>Per Container Recommended Market Value:</td>
                    <td style={{ textAlign: 'right', fontWeight: '500' }}>
                      ${Number(data.recommendedMarketValuePerBarrel).toFixed(2)}
                    </td>
                  </tr>
                  <tr>
                    <td>Total Recommended Insurance Value:</td>
                    <td style={{ textAlign: 'right', fontWeight: '500' }}>
                      {formatCurrency(data.recommendedInsuranceValue)}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      );
    }
    return null;
  };

  // Handle mouse over to show additional info
  const handleMouseOver = (data: any, index: number) => {
    setHoveredBar({
      data,
      index
    });
  };

  // Handle mouse leave
  const handleMouseLeave = () => {
    setHoveredBar(null);
  };

  const currentYear = new Date().getFullYear();

  const yearOptions = Array.from({ length: 4 }, (_, index) => {
    const year = currentYear - index;
    return { value: year, label: year };
  });


  // Additional info 
  const AdditionalInfoBox = (): JSX.Element | null => {
    if (!hoveredBar) return null;
    
    const { data } = hoveredBar;
    const valueDifference = data.currentInsuranceValue - data.recommendedInsuranceValue;
    const perBarrelDifference = data.currentMarketValuePerBarrel - data.recommendedMarketValuePerBarrel;
    
    return (
      <div className="position-absolute bg-dark text-white p-2 rounded shadow">
        <p className="fw-bold mb-1">Value Difference</p>
        <p className="small mb-0">Total: {formatCurrency(Math.abs(valueDifference))}</p>
        <p className="small mb-0">Per Container: ${Math.abs(perBarrelDifference).toFixed(2)}</p>
      </div>
    );
  };

  // Calculate max value for Y-axis domain
  const getMaxChartValue = () => {
    if (!data || data.length === 0) return 5000;
    
    const maxValue = Math.max(
      ...data.map(item => Math.max(item.currentInsuranceValue, item.recommendedInsuranceValue))
    );
    
    // Round up to nearest thousand for nice axis values
    return Math.ceil(maxValue / 1000) * 1000;
  };
  
  // Generate ticks for Y-axis
  const generateYAxisTicks = () => {
    const maxValue = getMaxChartValue();
    const tickCount = 5;
    const ticks = [];
    
    for (let i = 0; i <= tickCount; i++) {
      ticks.push((maxValue / tickCount) * i);
    }
    
    return ticks;
  };

  const handleExportClick = async() => {
    try {
          const response = await InventoryApiService.downloadMarketValueReport(params, selectedYear);
          if(response.status === 202) {
            CommonService.Toast.fire({
                      title: "Your export is in progress. We'll notify you when it's ready!",
                      icon: "success"
                    });
          }
          
    } catch (error) {
       console.log("Error occurred while exporting file")
    }
  }

  const processedData = data.map((item) => ({
    ...item,
    currentInsuranceValue: item.currentInsuranceValue ?? item.recommendedInsuranceValue,
  }));

  const handleBack = () => {
    history.goBack();
  };

  return (
    <div className="usersList">
      <div className="usersList__header--wrapper d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div className="d-flex align-items-center gap-2">
                  {/* {CommonService.isAdmin() && ( */}
                    <>
                      {CommonService.isAdmin() && (<IconButton
                        onClick={handleBack}
                        aria-label="ArrowBackIcon"
                        sx={{
                          backgroundColor: "#FFF",
                          "&:hover": { backgroundColor: "#FFF" },
                        }}
                      >
                       
                        <ArrowBackIcon sx={{ color: "#1C1B1F" }} />
                      </IconButton>
                       )}
      
                      {CommonService.isAdmin() && (
                        <>
                          <div className="header--title">
                            <h1 className="title mb-0">Insurance Values by Container Lot Graphical View</h1>
                          </div>
                          {/* <div className='divider mx-2'></div> */}
                      </>
                      )}

                      {/* As we have give the export option in table view so don't need to give export option in this history */}
                      {/* <button title="Export to Excel"
                        className="primary-btn d-inline-flex align-items-center me-1"
                        style={{ paddingTop:"4px",paddingLeft:11}}
                        onClick={handleExportClick}
                      >
                        <FileDownloadOutlinedIcon sx={{marginTop:"5.5px"}}/>
                        Export
                      </button> */}
                    </>
                  {/* )} */}
                </div>
                <div className='d-flex flex-wrap align-items-center gap-2'>
                  <select
                      id="year"
                      className="form-select"
                      value={selectedYear}
                      style={{ width: 'auto' }}
                      onChange={(e) => {
                        const selectedYear = Number(e.target.value);
                        setSelectedYear(selectedYear);
                        getMarketValueData(selectedLotId, selectedYear);
                      }}
                    >
                      {yearOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                  </select>

                    {/* Lot selector dropdown */}
                    <select
                      value={selectedLotId}
                      onChange={handleBarrelLotChange}
                      className="form-select me-3"
                      style={{ width: 'auto' }}
                    >
                      {lotsList?.map((lot) => (
                        <option key={lot} value={lot}>
                          {lot}
                        </option>
                      ))}
                    </select>
                </div>
              </div>
      <div className="user-table-container market-value-history-wrapper">
          <div className="userTable">
    <div className="pt-4 px-4 pb-3">
      {/* Filter options */}
      {/* <div className="d-flex align-items-center mb-4 gap-3 justify-content-end flex-wrap">
        <select
          id="year"
          className="form-select"
          value={selectedYear}
          style={{ width: 'auto' }}
          onChange={(e) => {
            const selectedYear = Number(e.target.value);
            setSelectedYear(selectedYear);
            getMarketValueData(selectedLotId, selectedYear);
          }}
        >
          {yearOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
       </select> */}

         {/* Lot selector dropdown */}
         {/* <select
          value={selectedLotId}
          onChange={handleBarrelLotChange}
          className="form-select me-3"
          style={{ width: 'auto' }}
        >
          {lotsList?.map((lot) => (
            <option key={lot} value={lot}>
              {lot}
            </option>
          ))}
        </select>

        <button title="Export to Excel"
          className="primary-btn d-inline-flex align-items-center me-1"
          style={{ paddingTop:"4px",paddingLeft:11}}
          onClick={handleExportClick}
        >
          <FileDownloadOutlinedIcon sx={{marginTop:"5.5px"}}/>
          Export
        </button> */}
        
        {/* Legend */}
        {/* <div className="d-flex align-items-center gap-4">
          <div className="d-flex align-items-center">
            <div className="me-2" style={{ width: '16px', height: '16px', backgroundColor: '#AECBD7' }}></div>
            <span className="small text-secondary">Total Current Insurance Value</span>
          </div>
          <div className="d-flex align-items-center">
            <div className="me-2" style={{ width: '16px', height: '16px', backgroundColor: '#4A55A2' }}></div>
            <span className="small text-secondary">Total Recommended Insurance Value</span>
          </div>
        </div> */}
      {/* </div> */}
      
      {/* Chart container */}
      <div className='overflow-x-auto'>
      <div className="w-100" style={{ height: 'calc(100vh - 350px)',minHeight: '400px',minWidth:"700px" }}>
        {data.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={processedData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              barGap={5}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.2} stroke="#000" />
              <XAxis 
                dataKey="quarterDisplay" 
                tickLine={false}
                axisLine={{ stroke: "#000" }} 
                padding={{ left: 10, right: 10 }}
                tick={{ fontSize: 12 }}
              />
              <XAxis 
                dataKey="year" 
                axisLine={false}
                tickLine={false}
                interval={0}
                tick={{ fontSize: 12 }}
                xAxisId="year"
                allowDuplicatedCategory={false}
              />
              <YAxis 
                axisLine={{ stroke: "#000" }} 
                tickLine={false}
                tick={{ fontSize: 12 }}
                tickFormatter={(tick) => `$${tick.toLocaleString()}`}
                domain={[0, getMaxChartValue()]}
                ticks={generateYAxisTicks()}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ opacity: 0.1 }} />
              <Bar 
                dataKey="currentInsuranceValue" 
                fill="#AECBD7" 
                radius={[4, 4, 0, 0]} 
                barSize={30} 
                onMouseOver={handleMouseOver}
                onMouseLeave={handleMouseLeave}
                name="Current Market Value"
              />
              <Bar 
                dataKey="recommendedInsuranceValue" 
                fill="#4A55A2" 
                radius={[4, 4, 0, 0]} 
                barSize={30}
                onMouseOver={handleMouseOver}
                onMouseLeave={handleMouseLeave}
                name="Recommended Market Value"
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="d-flex align-items-center justify-content-center h-100">
            {isLoading ? <Spinner/> : (<p className="text-secondary">No Details Available</p>)}
          </div>
        )}
      </div>
      <div className="d-flex align-items-center gap-4 justify-content-center py-3">
          <div className="d-flex align-items-center">
            <div className="me-2" style={{ width: '16px', height: '16px', backgroundColor: '#AECBD7' }}></div>
            <span className="small text-secondary">Total Current Insurance Value</span>
          </div>
          <div className="d-flex align-items-center">
            <div className="me-2" style={{ width: '16px', height: '16px', backgroundColor: '#4A55A2' }}></div>
            <span className="small text-secondary">Total Recommended Insurance Value</span>
          </div>
        </div>
        </div>
      
      {/* As of now We will not show the additional information on the chart (May 23, 2025) */}
      {/* {hoveredBar && <AdditionalInfoBox />} */}
    </div>
    </div>
    </div>
    </div>
  );
};

export default BarrelMarketValueChartView
